import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { IAmenity, IListing, IListingExtra, ISite, IUser } from '@curbnturf/entities';
import {
  amenityFormBuilder,
  CheckedComponent,
  DisplayValidationErrorsContainerDirective,
  DisplayValidationErrorsDirective,
  extraFormBuilder,
  IAmenityForm,
  IListingExtraForm,
  ListingForm,
  ToggleComponent,
} from '@curbnturf/form-helpers';
import { siteComplete } from '@curbnturf/helpers';
import { PriceComponent } from '@curbnturf/host-forms/src/lib/price/price.component';
import { ListingFacade } from '@curbnturf/listing';
import { SiteFacade } from '@curbnturf/site';
import { UserFacade } from '@curbnturf/user';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { ListingTaxesComponent } from '../listing-taxes/listing-taxes.component';

@Component({
  selector: 'curbnturf-payout',
  templateUrl: './payout.component.html',
  styleUrls: ['./payout.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsContainerDirective,
    FormsModule,
    IonicModule,
    ListingTaxesComponent,
    NgClass,
    NgFor,
    NgIf,
    PriceComponent,
    ReactiveFormsModule,
    ToggleComponent,
  ],
})
export class PayoutComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild(ListingTaxesComponent)
  listingTaxesComponent: ListingTaxesComponent;

  @Input() listing: IListing;
  @Input() site: ISite;

  @Output() saved: EventEmitter<boolean> = new EventEmitter();

  currentUser: IUser;

  payoutForm: FormGroup<{
    price: FormControl<number | null>;
    nextReservationDiscount: FormControl<number | null>;
    weekdayDiscount: FormControl<number | null>;
  }>;

  extrasForm: FormArray<FormGroup<IListingExtraForm>>;

  amenitiesForm: FormArray<FormGroup<IAmenityForm>>;

  siteComplete = siteComplete;

  get additionalOthers(): FormGroup<IListingExtraForm>[] | undefined {
    if (this.extras) {
      return this.extras.controls.filter((extra) => {
        if (extra.value.label) {
          const label: string = extra.value.label;
          return label.startsWith('other|');
        }

        return false;
      }) as FormGroup<IListingExtraForm>[];
    }

    return;
  }

  get amenities(): FormArray<FormGroup<IAmenityForm>> | undefined {
    if (this.amenitiesForm) {
      return this.amenitiesForm;
    }

    return;
  }

  get extras(): FormArray<FormGroup<IListingExtraForm>> | undefined {
    if (this.extrasForm) {
      return this.extrasForm;
    }

    return;
  }

  get extraParking(): FormGroup<IListingExtraForm> {
    if (this.extras) {
      let extraParking = this.extras.controls.find(
        (extra) => extra.value.label === 'extraParking',
      ) as FormGroup<IListingExtraForm>;

      if (!extraParking) {
        extraParking = extraFormBuilder(this.fb, { label: 'extraParking' });
        this.extras.push(extraParking);
      }

      return extraParking;
    }

    return extraFormBuilder(this.fb);
  }

  get extraTent(): FormGroup<IListingExtraForm> {
    if (this.extras) {
      let extraTent = this.extras.controls.find(
        (extra) => extra.value.label === 'extraTent',
      ) as FormGroup<IListingExtraForm>;

      if (!extraTent) {
        extraTent = extraFormBuilder(this.fb, { label: 'extraTent' });
        this.extras.push(extraTent);
      }

      return extraTent;
    }

    return extraFormBuilder(this.fb);
  }

  get firewood(): FormGroup<IListingExtraForm> | undefined {
    if (this.extras) {
      return this.extras.controls.find((extra) => extra.value.label === 'firewood') as FormGroup<IListingExtraForm>;
    }

    return;
  }

  get firewoodCount(): FormControl<number | null> | undefined {
    if (this.firewood) {
      return this.firewood.get('count') as FormControl<number | null>;
    }

    return;
  }

  get firewoodPrice(): FormControl<number | null> | undefined {
    if (this.firewood) {
      return this.firewood.get('price') as FormControl<number | null>;
    }

    return;
  }

  get lesson(): FormGroup<IListingExtraForm> | undefined {
    if (this.extras) {
      return this.extras.controls.find((extra) => extra.value.label === 'lesson') as FormGroup<IListingExtraForm>;
    }

    return;
  }

  get lessonCount(): FormControl<number | null> | undefined {
    if (this.lesson) {
      return this.lesson.get('count') as FormControl<number | null>;
    }

    return;
  }

  get lessonPrice(): FormControl<number | null> | undefined {
    if (this.lesson) {
      return this.lesson.get('price') as FormControl<number | null>;
    }

    return;
  }

  get lessonName(): FormControl<string | null> | undefined {
    if (this.lesson) {
      return this.lesson.get('name') as FormControl<string | null>;
    }

    return;
  }

  get lessonDescription(): FormControl<string | null> | undefined {
    if (this.lesson) {
      return this.lesson.get('description') as FormControl<string | null>;
    }

    return;
  }

  get meal(): FormGroup<IListingExtraForm> | undefined {
    if (this.extras) {
      return this.extras.controls.find((extra) => extra.value.label === 'meal') as FormGroup<IListingExtraForm>;
    }

    return;
  }

  get mealCount(): FormControl<number | null> | undefined {
    if (this.meal) {
      return this.meal.get('count') as FormControl<number | null>;
    }

    return;
  }

  get mealPrice(): FormControl<number | null> | undefined {
    if (this.meal) {
      return this.meal.get('price') as FormControl<number | null>;
    }

    return;
  }

  get mealName(): FormControl<string | null> | undefined {
    if (this.meal) {
      return this.meal.get('name') as FormControl<string | null>;
    }

    return;
  }

  get mealDescription(): FormControl<string | null> | undefined {
    if (this.meal) {
      return this.meal.get('description') as FormControl<string | null>;
    }

    return;
  }

  get nextReservationDiscount() {
    if (this.payoutForm) {
      return this.payoutForm.get('nextReservationDiscount') as FormControl<number | null>;
    }

    return;
  }

  get other(): FormGroup<IListingExtraForm> | undefined {
    if (this.extras) {
      return this.extras.controls.find((extra) => extra.value.label === 'other') as FormGroup<IListingExtraForm>;
    }

    return;
  }

  get otherCount(): FormControl<number | null> | undefined {
    if (this.other) {
      return this.other.get('count') as FormControl<number | null>;
    }

    return;
  }

  get otherPrice(): FormControl<number | null> | undefined {
    if (this.other) {
      return this.other.get('price') as FormControl<number | null>;
    }

    return;
  }

  get otherName(): FormControl<string | null> | undefined {
    if (this.other) {
      return this.other.get('name') as FormControl<string | null>;
    }

    return;
  }

  get otherDescription(): FormControl<string | null> | undefined {
    if (this.other) {
      return this.other.get('description') as FormControl<string | null>;
    }

    return;
  }

  get price() {
    if (this.payoutForm) {
      return this.payoutForm.get('price') as FormControl<number | null>;
    }

    return;
  }

  get tour(): FormGroup<IListingExtraForm> | undefined {
    if (this.extras) {
      return this.extras.controls.find((extra) => extra.value.label === 'tour') as FormGroup<IListingExtraForm>;
    }

    return;
  }

  get tourCount(): FormControl<number | null> | undefined {
    if (this.tour) {
      return this.tour.get('count') as FormControl<number | null>;
    }

    return;
  }

  get weekdayDiscount() {
    if (this.payoutForm) {
      return this.payoutForm.get('weekdayDiscount') as FormControl<number | null>;
    }

    return;
  }

  subscriptions: Subscription[] = [];
  formSubscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private siteFacade: SiteFacade,
    private listingFacade: ListingFacade,
    private alertFacade: AlertFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        if (user) {
          this.currentUser = user;
        }
      }),
    );

    this.createForms();
  }

  createForms() {
    this.payoutForm = this.fb.group({
      nextReservationDiscount: this.fb.control<number | null>(null, [Validators.min(0)]),
      weekdayDiscount: this.fb.control(0, [Validators.min(0)]),
      price: this.fb.control<number | null>(null, [Validators.required, Validators.min(10)]),
    });

    this.extrasForm = this.fb.array<FormGroup<IListingExtraForm>>([], { updateOn: 'blur' });

    this.amenitiesForm = this.fb.array<FormGroup<IAmenityForm>>([], { updateOn: 'blur' });

    this.payoutForm.patchValue({
      nextReservationDiscount: this.listing.nextReservationDiscount,
      weekdayDiscount: this.listing.weekdayDiscount,
      price: this.site.price,
    });

    this.extrasForm.clear();
    this.listing?.extras?.forEach((el) => {
      this.extrasForm.push(
        this.fb.group<IListingExtraForm>({
          count: this.fb.control<number | null>(el.count ?? null),
          name: this.fb.control<string | null>(el.name ?? null),
          description: this.fb.control<string | null>(el.description ?? null),
          price: this.fb.control<number | null>(el.price ?? null),
          label: this.fb.control<string | null>(el.label ?? null),
          id: this.fb.control<number | null>(el.id ?? null),
        }),
      );
    });

    setTimeout(() => {
      this.clearFormSubscriptions();

      this.formSubscriptions.push(
        this.payoutForm.valueChanges
          .pipe(
            tap(() => {
              this.payoutForm.markAsTouched();
              this.payoutForm.markAsDirty();
              this.price?.markAsTouched();
              this.price?.markAsDirty();
            }),
            debounceTime(1000),
          )
          .subscribe(() => {
            this.updatingPricing();
          }),
        this.extrasForm.valueChanges
          .pipe(
            tap(() => {
              this.extrasForm.markAsTouched();
              this.extrasForm.markAsDirty();
            }),
            debounceTime(1000),
          )
          .subscribe(() => {
            this.updateExtras();
          }),
        this.amenitiesForm.valueChanges
          .pipe(
            tap(() => {
              this.amenitiesForm.markAsTouched();
              this.amenitiesForm.markAsDirty();
            }),
            debounceTime(1000),
          )
          .subscribe(() => {
            this.updateAmenities();
          }),
      );
    }, 1500);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.site?.currentValue && changes.site.currentValue.id !== changes.site.previousValue?.id) {
      this.createForms();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.clearFormSubscriptions();
  }

  clearFormSubscriptions() {
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formSubscriptions = [];
  }

  addAdditionalOther() {
    let i = 0;
    if (this.extras && this.additionalOthers) {
      i = this.additionalOthers.length;
    }

    if (this.extras) {
      this.extras.push(extraFormBuilder(this.fb, { label: `other|${i}` }));
    }
  }

  updatingPricing() {
    if (this.payoutForm.valid) {
      // Update the user record with the following data
      const price = this.price?.value ?? 10;

      this.siteFacade.updatePricing({
        price,
      });

      this.listingFacade.updatePricing({
        nextReservationDiscount: this.nextReservationDiscount?.value ?? 0,
        weekdayDiscount: this.weekdayDiscount?.value ?? 0,
      });

      if (this.listing.sites?.length) {
        const siteIndex = this.listing.sites.findIndex((site) => site.id === this.site.id);

        if (siteIndex >= 0) {
          const site = {
            ...this.listing.sites[siteIndex],
            price,
          };

          this.listing.sites.splice(siteIndex, 1, site);
        }
      }

      this.listing.nextReservationDiscount = this.nextReservationDiscount?.value ?? 0;
      this.listing.weekdayDiscount = this.weekdayDiscount?.value ?? 0;
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  updateExtras() {
    if (this.extrasForm.valid && this.listing?.id) {
      const extras: IListingExtra[] = this.extrasForm?.controls
        .map((extra) => ListingForm.formatExtraFormValue(extra))
        .filter(
          (extra) =>
            extra.count !== undefined &&
            extra.count !== null &&
            extra.price !== undefined &&
            extra.price !== null &&
            (extra.count > 0 || extra.price > 0),
        ) as IAmenity[];
      if (extras.length) {
        this.listingFacade.updateExtras(this.listing.id, extras);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  updateAmenities() {
    if (this.amenitiesForm.valid && this.listing?.id) {
      const amenities = this.amenitiesForm?.controls.map((amenity) =>
        ListingForm.formatAmenityFormValue(amenity),
      ) as IAmenity[];
      this.siteFacade.updateAmenities(this.listing.id, amenities);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  save() {
    return this.listingTaxesComponent.save();
  }

  savedPassThrough(saved: boolean) {
    this.saved.emit(saved);
  }

  toggleAdditionalOther(i: number) {
    if (this.extras && (!this.additionalOthers || !this.additionalOthers[i])) {
      this.extras.push(extraFormBuilder(this.fb, { label: `other|${i}` }));
    }

    if (!this.additionalOthers) {
      return;
    }

    const count = this.additionalOthers[i].get('count');
    if (count) {
      if (count.value) {
        count.setValue(0);
      } else {
        count.setValue(100);
      }
    }

    if (this.additionalOthers[i]) {
      this.additionalOthers[i].markAsDirty();
    }
  }

  toggleFirewood() {
    if (this.extras && !this.firewood) {
      this.extras.push(extraFormBuilder(this.fb, { label: 'firewood' }));
    }

    if (this.firewoodCount) {
      if (this.firewoodCount.value) {
        this.firewoodCount.setValue(0);
      } else {
        this.firewoodCount.setValue(100);
      }
    }

    if (this.firewood) {
      this.firewood.markAsDirty();
    }
  }

  toggleLesson() {
    if (this.extras && !this.lesson) {
      this.extras.push(extraFormBuilder(this.fb, { label: 'lesson' }));
    }

    if (this.lessonCount) {
      if (this.lessonCount.value) {
        this.lessonCount.setValue(0);
      } else {
        this.lessonCount.setValue(100);
      }
    }

    if (this.lesson) {
      this.lesson.markAsDirty();
    }
  }

  toggleMeal() {
    if (this.extras && !this.meal) {
      this.extras.push(extraFormBuilder(this.fb, { label: 'meal' }));
    }

    if (this.mealCount) {
      if (this.mealCount.value) {
        this.mealCount.setValue(0);
      } else {
        this.mealCount.setValue(100);
      }
    }

    if (this.meal) {
      this.meal.markAsDirty();
    }
  }

  toggleOther() {
    if (this.extras && !this.other) {
      this.extras.push(extraFormBuilder(this.fb, { label: 'other' }));
    }

    if (this.otherCount) {
      if (this.otherCount.value) {
        this.otherCount.setValue(0);
      } else {
        this.otherCount.setValue(100);
      }
    }

    if (this.other) {
      this.other.markAsDirty();
    }
  }

  tentChange() {
    const tentAmenityIndex = this.amenities?.controls.findIndex((amenityValue) => amenityValue.value.type === 'tents');

    if ((!tentAmenityIndex || tentAmenityIndex === -1) && tentAmenityIndex !== 0 && this.extraTent?.value?.count) {
      this.amenities?.push(amenityFormBuilder(this.fb, { type: 'tents' }));
      return;
    }

    if (!this.extraTent?.value?.count && (tentAmenityIndex || tentAmenityIndex === 0) && tentAmenityIndex !== -1) {
      this.amenities?.removeAt(tentAmenityIndex);
    }
  }

  vehicleChange() {
    const vehicleAmenityIndex = this.amenities?.controls.findIndex(
      (amenityValue) => amenityValue.value.type === 'vehicles',
    );

    if (
      (!vehicleAmenityIndex || vehicleAmenityIndex === -1) &&
      vehicleAmenityIndex !== 0 &&
      this.extraParking?.value?.count
    ) {
      this.amenities?.push(amenityFormBuilder(this.fb, { type: 'vehicles' }));
      return;
    }

    if (
      !this.extraParking?.value?.count &&
      (vehicleAmenityIndex || vehicleAmenityIndex === 0) &&
      vehicleAmenityIndex !== -1
    ) {
      this.amenities?.removeAt(vehicleAmenityIndex);
    }
  }

  updateNextReservationDiscount(value: number) {
    if (this.nextReservationDiscount) {
      this.nextReservationDiscount.setValue(value);
      this.nextReservationDiscount.markAsDirty();
    }
  }
}
